import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { BadgeColor } from '../badge-single-color/model';
import { GridMapColorPair } from '../../helpers/widget-color.helper';
import { BadgeMultiColorWidgetConfig } from './model';
import {
  resolveBadgeColorsFromValue,
} from '../../helpers/widget-color.helper';

@Component({
  selector: 'app-badge-multi-color-widget',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatTooltipModule, MatIconModule],
  templateUrl: './badge-multi-color-widget.component.html',
  styleUrls: ['./badge-multi-color-widget.component.scss'],
})
export class BadgeMultiColorWidgetComponent {
  @Input() value: any = '';
  @Input() colorMap?: Record<string, BadgeColor | string | GridMapColorPair>;
  @Input() evaluator?: (value: any) => BadgeColor | string | GridMapColorPair;
  @Input() tooltip?: string | ((value: any) => string);
  @Input() icon?: string; // Optional Material Icon name
  @Input() action?: (value: any) => void; // Optional action callback
  @Input() translate: boolean = false;
  @Input() translationPrefix?: string;
  @Input() translationKey?: (value: any) => string;

  /**
   * Get the background color for the badge.
   * Priority: evaluator > colorMap > static gray fallback.
   * Supports hex colors and GridMapColorPair entries.
   */
  get badgeBackgroundColor(): string {
    return this.getBadgeColors().backgroundColor;
  }

  get badgeTextColor(): string {
    return this.getBadgeColors().textColor;
  }

  private getBadgeColors() {
    let colorValue: BadgeColor | string | GridMapColorPair | undefined;
    const strValue = String(this.value).trim();

    if (this.evaluator) {
      colorValue = this.evaluator(this.value);
    } else if (this.colorMap) {
      colorValue = this.colorMap[strValue];
    }

    return resolveBadgeColorsFromValue(colorValue);
  }

  get displayValue(): string {
    const strValue = String(this.value).trim();
    return strValue.charAt(0).toUpperCase() + strValue.slice(1);
  }

  /**
   * Get the full translation key for i18n lookup
   * If translate is true, uses translationPrefix + normalized value
   * Example: USERS_DEMO_2.USERS_TABLE.ROLE + "ADMIN" -> USERS_DEMO_2.USERS_TABLE.ROLE.ADMIN
   */
  get translationKeyFull(): string | null {
    if (!this.translate) {
      return null;
    }

    if (this.translationKey) {
      return this.translationKey(this.value);
    }

    if (!this.translationPrefix) {
      return null;
    }

    const normalizedValue = String(this.value)
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');

    return `${this.translationPrefix}.${normalizedValue}`;
  }

  getTooltip(): string {
    if (!this.tooltip) {
      return '';
    }

    if (typeof this.tooltip === 'function') {
      return this.tooltip(this.value);
    }

    return this.tooltip;
  }

  isTooltipEnabled(): boolean {
    return this.tooltip !== undefined && this.tooltip !== null;
  }

  onClick(event: MouseEvent): void {
    if (this.action) {
      event.preventDefault();
      event.stopPropagation();
      this.action(this.value);
    }
  }
}
